/**
 * Thinking Content Filter
 * 
 * Simple filter: if a sentence contains any problematic words/phrases, delete the entire sentence.
 */

/**
 * Problematic words/phrases - if ANY of these appear in a sentence, remove the whole sentence
 */
const PROBLEMATIC_WORDS = [
  "guidelines",
  "per my instructions", 
  "I should respond",
  "I should give",
  "I should focus",
  "I should emphasize", 
  "I should avoid",
  "I should mention",
  "I should note",
  "I should keep",
  "I need to be careful",
  "I must be careful",
  "system architecture",
  "internal system",
  "technical implementation details",
  "how the thinking feature works internally",
  "chain of thought",
  "internal reasoning process",
  "AI capabilities",
  "philosophical discussions about AI",
  "as per my",
  "according to my",
  "I'm instructed to",
  "I'm told to",
  "I'm programmed to",
  "The user is asking about",
  "Looking at the context, Blake",
  "This seems to be a technical/product question",
  "This appears to be",
  "This is likely referring to",
  "The question suggests"
];

/**
 * Filter thinking content to remove problematic internal commentary
 * while preserving valuable reasoning and analysis
 * 
 * @param {string} content - Raw thinking content from AI
 * @returns {string} - Filtered content safe for user display
 */
export const filterThinkingContent = (content) => {
  if (!content || typeof content !== 'string') {
    return content;
  }

  // Split into lines to preserve markdown structure
  const lines = content.split('\n');
  
  // Filter out lines that contain problematic words
  const cleanLines = lines.filter(line => {
    const lowerLine = line.toLowerCase();
    
    // Check if line contains any problematic words/phrases
    return !PROBLEMATIC_WORDS.some(word => lowerLine.includes(word.toLowerCase()));
  });
  
  // Join lines back together with original formatting
  let result = cleanLines.join('\n').trim();
  
  // Clean up excessive empty lines (more than 2 consecutive)
  result = result.replace(/\n\s*\n\s*\n/g, '\n\n');
  
  // Fallback if nothing left
  if (result.length < 10) {
    return "Analyzing the question and considering the best approach to help...";
  }
  
  return result;
};

/**
 * Check if thinking content contains problematic patterns
 * Useful for debugging and monitoring filter effectiveness
 * 
 * @param {string} content - Thinking content to analyze
 * @returns {object} - Analysis of problematic patterns found
 */
export const analyzeThinkingContent = (content) => {
  if (!content) return { hasProblematicContent: false, words: [] };

  const foundWords = [];
  const lowerContent = content.toLowerCase();
  
  PROBLEMATIC_WORDS.forEach(word => {
    if (lowerContent.includes(word.toLowerCase())) {
      foundWords.push(word);
    }
  });

  return {
    hasProblematicContent: foundWords.length > 0,
    words: foundWords,
    originalLength: content.length,
    filteredLength: filterThinkingContent(content).length
  };
};

export default filterThinkingContent;